/* ==================================================================
   SCANDALOUS — scroll animation engine (preview)
   - One rAF loop, driven by a smoothed (lerped) scroll position so
     notched mouse wheels and trackpads both feel continuous.
   - Frame-rate independent smoothing (60Hz / 120Hz behave the same).
   - Layout is measured only on load/resize, never inside the loop;
     the loop only writes CSS variables (transform/opacity in CSS).
   - Respects prefers-reduced-motion.
   ================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;

  /* ---------- helpers ---------- */
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function seg(p, a, b) { return clamp01((p - a) / (b - a)); }
  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }
  function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function setVar(el, cache, name, val, unit) {
    val = Math.round(val * 10000) / 10000;
    if (cache[name] === val) return;
    cache[name] = val;
    el.style.setProperty(name, unit ? val + unit : String(val));
  }

  /* ---------- fade-up reveals ---------- */
  function setupReveals() {
    if (reduce || !('IntersectionObserver' in window)) return;
    var groups = [
      ['.sd-page-intro__band-inner > *', 0.1],
      ['.sd-story__content > *', 0.08],
      ['.sd-collection .sd-section-heading, .sd-collection .sd-section-sub', 0.06],
      ['.sd-grid > .sd-card', 0.09],
      ['.sd-gift__media, .sd-gift__content', 0.14],
      ['.sd-split__content > *', 0.08],
      ['.sd-values .sd-section-heading', 0],
      ['.sd-values__item', 0.07],
      ['.sd-edp__content > *', 0.1],
      ['.faq-header, .faq-item', 0.05],
      ['.sd-perks__item', 0.08]
    ];
    var vh = window.innerHeight;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    root.classList.add('sd-anim');
    groups.forEach(function (g) {
      var list = document.querySelectorAll(g[0]);
      Array.prototype.forEach.call(list, function (el, i) {
        if (el.getBoundingClientRect().top < vh * 0.9) return; // already on screen: never hide it
        el.classList.add('sd-rv');
        el.style.setProperty('--rd', ((i % 4) * g[1]).toFixed(2) + 's');
        io.observe(el);
      });
    });
  }

  /* ---------- scroll-linked pieces ---------- */
  var stories = [];
  var parallaxEls = [];
  var hero = null;
  var vh = window.innerHeight;
  var target = window.scrollY;
  var current = target;
  var running = false;
  var lastTs = 0;

  function measure() {
    vh = window.innerHeight;
    var sy = window.scrollY;
    stories.forEach(function (s) {
      var r = s.track.getBoundingClientRect();
      var stageH = s.stage.offsetHeight;
      s.start = r.top + sy - vh * 0.6;               // begin a little before it pins
      s.dist = Math.max(1, r.height - stageH + vh * 0.6);
    });
    parallaxEls.forEach(function (o) {
      var r = o.box.getBoundingClientRect();
      o.top = r.top + sy;
      o.h = r.height;
    });
    if (hero) {
      var hr = hero.stage.getBoundingClientRect();
      hero.top = hr.top + sy;
      hero.h = Math.max(1, hr.height);
    }
  }

  function render(y) {
    for (var i = 0; i < stories.length; i++) {
      var s = stories[i];
      if (y + vh < s.start - vh || y > s.start + s.dist + vh) continue;
      var p = clamp01((y - s.start) / s.dist);
      var a = easeOut(seg(p, 0.0, 0.2));
      var b = easeInOut(seg(p, 0.24, 0.6));
      var c = easeOut(seg(p, 0.6, 0.76));
      var d = easeOut(seg(p, 0.76, 0.88));
      setVar(s.stage, s.cache, '--p', p);
      setVar(s.stage, s.cache, '--a', a);
      setVar(s.stage, s.cache, '--b', b);
      setVar(s.stage, s.cache, '--c', c);
      setVar(s.stage, s.cache, '--d', d);
      var assembled = c > 0.6;
      if (assembled !== s.assembled) {
        s.assembled = assembled;
        s.stage.classList.toggle('is-assembled', assembled);
        s.links.forEach(function (l) { l.tabIndex = assembled ? 0 : -1; });
      }
    }

    for (var j = 0; j < parallaxEls.length; j++) {
      var o = parallaxEls[j];
      if (y + vh < o.top - 200 || y > o.top + o.h + 200) continue;
      var off = (o.top + o.h / 2) - (y + vh / 2);
      var py = -off * o.speed;
      var max = o.h * 0.065;
      if (py > max) py = max;
      if (py < -max) py = -max;
      setVar(o.el, o.cache, '--py', py, 'px');
    }

    if (hero) {
      if (y < hero.top + hero.h + vh) {
        setVar(hero.stage, hero.cache, '--h', clamp01((y - hero.top + 120) / hero.h));
      }
    }
  }

  function tick(ts) {
    var dt = lastTs ? Math.min(64, ts - lastTs) : 16.67;
    lastTs = ts;
    var diff = target - current;
    if (Math.abs(diff) < 0.4) {
      current = target;
    } else {
      // ~0.13 per 60Hz frame, scaled for the actual frame time
      var k = 1 - Math.pow(1 - 0.13, dt / 16.67);
      current += diff * k;
    }
    render(current);
    if (current !== target) {
      requestAnimationFrame(tick);
    } else {
      running = false;
      lastTs = 0;
    }
  }

  function kick() {
    if (!running) {
      running = true;
      requestAnimationFrame(tick);
    }
  }

  function onScroll() {
    target = window.scrollY;
    kick();
  }

  var resizeQueued = false;
  function onResize() {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(function () {
      resizeQueued = false;
      measure();
      target = window.scrollY;
      render(current);
      kick();
    });
  }

  function setupScroll() {
    if (reduce) return;

    Array.prototype.forEach.call(document.querySelectorAll('[data-sd-ss]'), function (sec) {
      var track = sec.querySelector('.sd-ss__track');
      var stage = sec.querySelector('.sd-ss__stage');
      if (!track || !stage) return;
      stories.push({
        track: track,
        stage: stage,
        cache: {},
        assembled: false,
        links: Array.prototype.slice.call(stage.querySelectorAll('.sd-ss__link'))
      });
    });

    [
      ['.sd-story__img', 0.12],
      ['.sd-edp__img, .sd-edp__video', 0.1],
      ['.sd-split__img', 0.08]
    ].forEach(function (g) {
      Array.prototype.forEach.call(document.querySelectorAll(g[0]), function (el) {
        if (!el.parentElement) return;
        el.setAttribute('data-sd-parallax', '');
        parallaxEls.push({ el: el, box: el.parentElement, speed: g[1], cache: {} });
      });
    });

    var hs = document.querySelector('.sd-hero-video__stage');
    if (hs) hero = { stage: hs, cache: {} };

    measure();
    current = target = window.scrollY;
    render(current);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', onResize);
    if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(document.body);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(onResize);
  }

  function init() {
    try { setupReveals(); } catch (e) {}
    try { setupScroll(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
